const CLOUDINARY_CLOUD_NAME = "big-oven-tees-inc";
const CLOUDINARY_UPLOAD_PRESET = "tnedst8q";
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/upload`;

export const getDtfTransfersFolder = (emailid) =>
  `Shirtly/${emailid}/DTFTransfers`;

export const getDtfGangSheetFolder = (emailid) =>
  `Shirtly/${emailid}/DTFGangSheets`;

export const isImageFile = (file) => {
  if (!file?.type) return false;
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")) {
    return false;
  }
  return file.type.startsWith("image/");
};

export const uploadFileToCloudinary = async (file, emailid, folder) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("folder", folder || getDtfTransfersFolder(emailid));

  const response = await fetch(CLOUDINARY_UPLOAD_URL, {
    method: "post",
    body: formData,
  });

  if (!response.ok) {
    throw new Error("Cloudinary upload failed");
  }

  return response.json();
};

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  if (!response.ok) {
    throw new Error("Failed to read exported image");
  }
  return response.blob();
};

export const uploadDataUrlToCloudinary = async (
  dataUrl,
  emailid,
  folder,
  fileName,
) => {
  if (!dataUrl) {
    throw new Error("Missing exported image data");
  }

  const formData = new FormData();
  try {
    const blob = await dataUrlToBlob(dataUrl);
    const safeName = `${(fileName || "gang-sheet").replace(/[^\w.-]+/g, "_")}.jpg`;
    formData.append("file", blob, safeName);
  } catch {
    formData.append("file", dataUrl);
  }

  formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  formData.append("folder", folder || getDtfGangSheetFolder(emailid));

  const response = await fetch(CLOUDINARY_UPLOAD_URL, {
    method: "post",
    body: formData,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.error) {
    throw new Error(
      data?.error?.message ||
        data?.message ||
        `Cloudinary upload failed (${response.status})`,
    );
  }

  if (!data?.secure_url && !data?.url) {
    throw new Error("Cloudinary upload did not return an image URL");
  }

  return data;
};

export const openCloudinaryUploadWidget = (
  emailid,
  onSuccess,
  {
    folder,
    multiple = false,
    maxFiles = 1,
    clientAllowedFormats = ["png", "jpeg", "jpg", "pdf", "svg", "ai", "eps"],
  } = {},
) => {
  const myWidget = window.cloudinary.createUploadWidget(
    {
      cloudName: CLOUDINARY_CLOUD_NAME,
      show_powered_by: false,
      uploadPreset: CLOUDINARY_UPLOAD_PRESET,
      folder: folder || getDtfTransfersFolder(emailid),
      clientAllowedFormats,
      sources: ["local", "camera", "url"],
      multiple,
      maxFiles,
    },
    (error, result) => {
      if (!error && result?.event === "success") {
        onSuccess(result.info);
        if (!multiple) {
          myWidget.close();
        }
      }
      if (!error && (result?.event === "queue-completed" || result?.event === "queue-complete")) {
        myWidget.close();
      }
    },
  );

  myWidget.open();
};

export const applyCloudinaryBackgroundRemoval = (secureUrl) => {
  if (!secureUrl?.includes("/upload/")) {
    return secureUrl;
  }

  const [prefix, suffix] = secureUrl.split("/upload/");
  return `${prefix}/upload/e_background_removal/${suffix}`;
};

export const applyCloudinaryUpscale = (secureUrl) => {
  if (!secureUrl?.includes("/upload/")) {
    return secureUrl;
  }

  const [prefix, suffix] = secureUrl.split("/upload/");
  return `${prefix}/upload/e_upscale/${suffix}`;
};

export const applyCloudinaryHalftone = (secureUrl) => {
  if (!secureUrl?.includes("/upload/")) {
    return secureUrl;
  }

  const [prefix, suffix] = secureUrl.split("/upload/");
  return `${prefix}/upload/e_art:zorro/${suffix}`;
};

export const preloadCloudinaryImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(url);
    image.onerror = () => {
      const retryUrl = `${url}${url.includes("?") ? "&" : "?"}timestamp=${Date.now()}`;
      const retryImage = new Image();
      retryImage.crossOrigin = "anonymous";
      retryImage.onload = () => resolve(retryUrl);
      retryImage.onerror = reject;
      retryImage.src = retryUrl;
    };
    image.src = url;
  });
